import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, ShieldAlert, Eye, EyeOff } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_pitch-connect-40/artifacts/yd54d9mb_D6EC86DD-43FE-4578-97EA-A54FBCABC788.png";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("This reset link is missing its token. Request a new one.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/password-reset/confirm", { token, new_password: password });
      setDone(true);
      await refresh();
      toast.success("Password reset. You're logged in.");
      // Mirror Login's logic — send users to onboarding if they never finished it.
      const dest = data?.user?.profile_complete ? "/discover" : "/onboarding";
      setTimeout(() => nav(dest, { replace: true }), 1200);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10 bg-footy-bg grain">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8" data-testid="logo-link">
          <img src={LOGO} alt="" className="w-14 h-14 rounded-full" />
          <span className="font-display text-3xl tracking-wider">FOOTYMATCH</span>
        </Link>
        <div className="footy-card p-7">
          {done ? (
            <div className="text-center" data-testid="reset-done">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center mb-4">
                <CheckCircle2 size={30} />
              </div>
              <h1 className="font-display text-3xl uppercase tracking-tight">Password reset</h1>
              <p className="text-slate-400 mt-3 text-sm">You&apos;re signed in. Taking you to Discover…</p>
            </div>
          ) : (
            <>
              <h1 className="font-display text-4xl uppercase tracking-tight">New password</h1>
              <p className="text-slate-400 mt-2 text-sm">Pick a new password — 8 characters or more.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="footy-label">New password</label>
                  <div className="relative">
                    <input type={show ? "text" : "password"} required autoFocus value={password}
                      onChange={(e)=>setPassword(e.target.value)}
                      placeholder="••••••••" className="footy-input pr-11" data-testid="reset-password" />
                    <button type="button" onClick={()=>setShow(s=>!s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                      data-testid="reset-toggle-visibility" tabIndex={-1} aria-label={show ? "Hide password" : "Show password"}>
                      {show ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="footy-label">Confirm new password</label>
                  <input type={show ? "text" : "password"} required value={confirm}
                    onChange={(e)=>setConfirm(e.target.value)}
                    placeholder="••••••••" className="footy-input" data-testid="reset-confirm" />
                </div>
                {error && (
                  <div className="text-sm text-footy-red bg-footy-red/10 border border-footy-red/30 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="reset-error">
                    <ShieldAlert size={16} className="flex-none mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}
                <button type="submit" disabled={loading || !token || !password || !confirm} className="footy-btn-primary w-full" data-testid="reset-submit">
                  {loading ? "Resetting..." : "Reset password"}
                </button>
              </form>
              <p className="text-sm text-slate-400 mt-6 text-center">
                Link expired? <Link to="/forgot-password" className="text-footy-red font-semibold" data-testid="link-forgot-again">Request a new one</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
